import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  queryCollection, 
  updateDocument,
  clearCollection,
  createCommHubUser,
  updateCommHubUser,
  getCommHubUsers,
  getApartment,
  Apartment,
  COMMHUB_APP_ID
} from '../lib/api';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  User, 
  EnvelopeSimple, 
  Phone, 
  House,
  SpinnerGap,
  Trash
} from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

interface ResidentInfo {
  id: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  email: string;
  phone: string;
  is_invoice_recipient?: boolean;
}

// Helper to get display name from resident
const getResidentName = (resident: ResidentInfo): string => {
  if (resident.first_name || resident.last_name) {
    return `${resident.first_name || ''} ${resident.last_name || ''}`.trim();
  }
  return resident.name || 'Okänt namn';
};

interface PendingUpdate {
  id: string;
  organization_id: string;
  apartment_id: string;
  residents: ResidentInfo[];
  verification_token: string;
  email_verified: boolean;
  phone_verified: boolean;
  status?: string;
  created_at: string;
  expires_at: string;
  verified_at?: string;
  completed?: boolean;
  completed_at?: string;
}

interface PendingMemberUpdatesProps {
  onUpdateProcessed?: () => void;
}

export default function PendingMemberUpdates({ onUpdateProcessed }: PendingMemberUpdatesProps) {
  const { user } = useAuth();
  const [updates, setUpdates] = useState<PendingUpdate[]>([]);
  const [apartments, setApartments] = useState<Record<string, Apartment>>({});
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);

  useEffect(() => {
    loadPendingUpdates();
  }, [user?.organization_id]);

  const loadPendingUpdates = async () => {
    if (!user?.organization_id) {
      return;
    }
    
    try {
      // Get pending updates for this organization (bypass cache to always get fresh data)
      const allUpdates = await queryCollection<PendingUpdate>('member_updates', {
        organization_id: user.organization_id
      }, { bypassCache: true });
      
      // Filter to only show email-verified, non-completed updates
      const pendingUpdates = allUpdates.filter(u => 
        u.email_verified && !u.completed
      ).sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      
      setUpdates(pendingUpdates);
      
      // Load apartment info for each update (in background, don't block rendering)
      const aptIds = [...new Set(pendingUpdates.map(u => u.apartment_id))];
      
      // Set loading to false immediately so UI shows
      setLoading(false);
      
      // Then load apartments in background
      const aptData: Record<string, Apartment> = {};
      for (const aptId of aptIds) {
        try {
          const response = await getApartment(aptId);
          if (response.data) {
            aptData[aptId] = response.data;
          }
        } catch (e) {
          console.error('Failed to load apartment:', aptId, e);
        }
      }
      
      setApartments(aptData);
    } catch (error) {
      console.error('Failed to load pending updates:', error);
      setLoading(false);
    }
  };

  const approveUpdate = async (update: PendingUpdate) => {
    setProcessing(update.id);
    
    try {
      console.log('Approving update:', update);

      // Process each resident in the update
      for (const resident of update.residents) {
        const displayName = getResidentName(resident);
        if (!displayName || displayName === 'Okänt namn' || !resident.email) continue;

        // User data for CommHub - use first_name/last_name if available, otherwise split name
        const userData = {
          first_name: resident.first_name || displayName.split(' ')[0],
          last_name: resident.last_name || displayName.split(' ').slice(1).join(' ') || '',
          email: resident.email,
          phone: resident.phone || undefined,
          apartment_id: update.apartment_id,
          organization_id: update.organization_id,
          role: 'resident' as const,
          is_active: true,
          is_invoice_recipient: resident.is_invoice_recipient || false
        };

        try {
          await createCommHubUser(userData);
        } catch (createError: unknown) {
          // Check if error is "user already exists" - then update instead
          const errorMessage = createError instanceof Error ? createError.message : String(createError);
          console.log('Create user error:', errorMessage);
          
          if (errorMessage.includes('already exists') || errorMessage.includes('duplicate') || errorMessage.includes('already registered')) {
            console.log('User already exists, updating instead:', resident.email);
            try {
              // Find the existing user by email using CommHub Users API
              const allUsers = await getCommHubUsers({ 
                organization_id: update.organization_id,
                limit: 500 
              });
              const existingUser = allUsers.find(
                (u: { id: string; email?: string }) => u.email?.toLowerCase() === resident.email.toLowerCase()
              );
              
              if (existingUser) {
                await updateCommHubUser(existingUser.id, {
                  first_name: userData.first_name,
                  last_name: userData.last_name,
                  phone: userData.phone,
                  apartment_id: userData.apartment_id,
                  is_invoice_recipient: userData.is_invoice_recipient,
                  // Ensure app_memberships is set for existing users
                  app_memberships: [{ app_id: COMMHUB_APP_ID, role: 'user' }]
                });
                console.log('User updated successfully with app_memberships:', existingUser.id);
              } else {
                console.log('Could not find existing user to update');
                throw new Error('Användaren finns redan men kunde inte hittas för uppdatering');
              }
            } catch (updateError) {
              console.error('Failed to update existing user:', updateError);
              throw updateError;
            }
          } else {
            console.error('Failed to create user:', createError);
            throw createError;
          }
        }
      }

      // Mark update as completed
      await updateDocument('member_updates', update.id, {
        completed: true,
        completed_at: new Date().toISOString(),
        approved_by: user?.id
      });
      console.log('Update marked as completed');

      toast.success('Medlemsuppgifter godkända och sparade');
      loadPendingUpdates();
      onUpdateProcessed?.();
    } catch (error) {
      console.error('Failed to approve update:', error);
      toast.error('Kunde inte godkänna uppdateringen');
    } finally {
      setProcessing(null);
    }
  };

  const rejectUpdate = async (update: PendingUpdate) => {
    setProcessing(update.id);
    
    try {
      console.log('Rejecting update:', update);
      
      await updateDocument('member_updates', update.id, {
        completed: true,
        rejected: true,
        completed_at: new Date().toISOString(),
        rejected_by: user?.id
      });
      console.log('Update marked as rejected');

      toast.success('Uppdateringen har avvisats');
      loadPendingUpdates();
    } catch (error) {
      console.error('Failed to reject update:', error);
      toast.error('Kunde inte avvisa uppdateringen');
    } finally {
      setProcessing(null);
    }
  };

  const clearAllUpdates = async () => {
    if (!window.confirm('Är du säker på att du vill ta bort alla väntande uppdateringar?')) {
      return;
    }
    
    setProcessing('clearing');
    try {
      const result = await clearCollection('member_updates');
      toast.success(`${result.deleted} uppdateringar borttagna`);
      loadPendingUpdates();
    } catch (error) {
      console.error('Failed to clear updates:', error);
      toast.error('Kunde inte rensa uppdateringar');
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex items-center justify-center">
          <SpinnerGap size={24} className="animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  if (updates.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Clock size={20} className="text-primary" />
            Väntande medlemsuppdateringar
          </CardTitle>
          <CardDescription>
            Uppdateringar som medlemmar har skickat in via den publika portalen
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-slate-500">
            <CheckCircle size={48} className="mx-auto mb-3 text-slate-300" />
            <p>Inga väntande uppdateringar</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock size={20} className="text-primary" />
              Väntande medlemsuppdateringar
              <Badge variant="secondary" className="ml-2">{updates.length}</Badge>
            </CardTitle>
            <CardDescription>
              Granska och godkänn uppdateringar som medlemmar har skickat in
            </CardDescription>
          </div>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={clearAllUpdates}
            disabled={processing === 'clearing'}
            className="text-red-500 hover:text-red-700 hover:bg-red-50"
          >
            <Trash size={16} className="mr-1" />
            Rensa alla
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {updates.map((update) => {
          const apartment = apartments[update.apartment_id];
          const isProcessing = processing === update.id;
          
          return (
            <div 
              key={update.id} 
              className="border rounded-lg p-4 space-y-3 bg-slate-50"
            >
              {/* Header */}
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <House size={18} className="text-slate-500" />
                  <span className="font-medium">
                    Lägenhet {apartment?.apartment_number || update.apartment_id}
                  </span>
                  {update.phone_verified && (
                    <Badge variant="outline" className="text-xs">
                      <Phone size={10} className="mr-1" /> Telefon verifierad
                    </Badge>
                  )}
                </div>
                <span className="text-xs text-slate-500">
                  {formatDate(update.created_at)}
                </span>
              </div>

              {/* Residents */}
              <div className="space-y-2">
                {update.residents.map((resident, idx) => (
                  <div key={idx} className="bg-white rounded p-3 border border-slate-200">
                    <div className="flex items-center gap-2 font-medium text-sm">
                      <User size={14} className="text-slate-400" />
                      {getResidentName(resident)}
                      {resident.is_invoice_recipient && (
                        <Badge variant="outline" className="text-xs text-red-600 border-red-200">
                          Fakturamottagare
                        </Badge>
                      )}
                    </div>
                    <div className="mt-1 space-y-0.5 text-xs text-slate-500">
                      <div className="flex items-center gap-1">
                        <EnvelopeSimple size={12} />
                        {resident.email}
                      </div>
                      {resident.phone && (
                        <div className="flex items-center gap-1">
                          <Phone size={12} />
                          {resident.phone}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button 
                  size="sm" 
                  onClick={() => approveUpdate(update)}
                  disabled={isProcessing}
                  className="flex-1"
                >
                  {isProcessing ? (
                    <SpinnerGap size={14} className="mr-1 animate-spin" />
                  ) : (
                    <CheckCircle size={14} className="mr-1" />
                  )}
                  Godkänn
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => rejectUpdate(update)}
                  disabled={isProcessing}
                >
                  <XCircle size={14} className="mr-1" />
                  Avvisa
                </Button>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
